import { Observable } from 'rxjs';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { LeftSidebarListItemConvertableModel } from '../../../Common/Layout/models/left-sidebar-list-item-convertable.model';
import { LeftSidebarLocalizations } from '../../../Common/Layout/models/left-sidebar-localizations.model';
import { UserAppPreferenceCommonFilters } from '../../../Core/Users/models/user-app-preferences-filters.model';
import { RouteType } from '../../../Monitor/Shared/enums/route-type.enum';
import { LeftSidebarListItem } from '../../../Common/Layout/models/left-sidebar-list-item.model';
import { BriefingStatusEnum } from '../../../Common/MonitorShared/enum/briefing-status.enum';
import { FilterOptionEnumLabel } from '../../../Common/MonitorShared/misc/filter-option-enum-label';
import { MediaSubscriptionStatusEnum } from '../../../Monitor/Shared/enums/media-subscription-status.enum';
import { allowedMediaSubscriptionEnum } from '../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { Mode} from '../../../Common/Layout/enums/mode.enum';
import { AlertStatusEnum, AlertTypeEnum } from '../../../Common/MonitorShared/enum/alert-type.enum';
import { BriefingDeliveryMethodEnum } from '../../Briefings/enums/briefing-delivery-method.enum';

type FilterOptions = {
    /**
     * Since the filters are constant instance objects, each config can filter out the
     * unneeded ones using a filterId.
     */
    filterId: string;
    /**
     * Property key that matches UserAppPreference Filter keys
     */
    prefProp: string;
    /**
     * Localization to use for this filter if needed
     */
    label?: string;
    /**
     * Status and type filters are enums, and need a prefix label
     */
    optionLabel?: FilterOptionEnumLabel;
}[];

/**
 * The static values for each mode. Instance values (MonitoringLeftSidebarModeConfigContext) must be registered
 * by the LHP Service
 */
interface MonitoringLeftSidebarModeConfigBase {
    /**
     * Route Types to match on for this config
     */
    readonly routeTypes: RouteType[];
    /**
     * Localization to show in the filter bar
     */
    readonly quickFilterPlaceholder: string;
    readonly localizations: LeftSidebarLocalizations;
    /**
     * Which filters to include and how they map to UserPreferences
     */
    readonly filterOptions: FilterOptions;
}

/**
 * The instance data that must come from the LHP Service, describing what the list item data is,
 * and how to convert the list to a LeftSidebarListItem list
 */
export interface MonitoringLeftSidebarModeConfigContext<T> {
    listItemSource: T[];
    listItemStream: Observable<T[]>;
    convertToSidebarListItems: (items: T[]) => LeftSidebarListItem[];
    topListItem?: LeftSidebarListItem;
}

const commonFilterOptions: FilterOptions = [
    // Name filter to be provided in with label value
    { filterId: 'createdBy', prefProp: 'createdBy' },
    { filterId: 'modifiedBy', prefProp: 'modifiedBy' },
    { filterId: 'createdDate', prefProp: 'createdDate' },
    { filterId: 'modifiedDate', prefProp: 'modifiedDate' },
    { filterId: 'visibility', prefProp: 'visibility' },
    { filterId: 'group', prefProp: 'groupId' },

];

/**
 * Maps the static values for each Mode, including which filters each mode uses
 */
const baseConfigMap: { [M in Mode]?: MonitoringLeftSidebarModeConfigBase } = {
    [Mode.newSearch]: {
        routeTypes: [
            RouteType.search,
            RouteType.results,
            RouteType.topicsHype,
            RouteType.topic,
        ],
        localizations: {
            amplifyButton: 'Monitor.leftSideBar.topics.amplifyButton',
            itemsHeading: 'Monitor.leftSideBar.topics.itemsHeading',
            statusHeading: '',
            noItems: 'Monitor.leftSideBar.topics.noItems'
        },
        quickFilterPlaceholder: 'Monitor.leftSidebar.topics.filter.quick',
        filterOptions: [
            { filterId: 'name', prefProp: 'name', label: 'Monitoring.search.topicName' },
            { filterId: 'autotags', prefProp: 'autoTag' },
            { filterId: 'autoSaving', prefProp: 'autoSaving' },
        ]
    },
    [Mode.topics]: {
        routeTypes: [
            RouteType.search,
            RouteType.results,
            RouteType.topicsHype,
            RouteType.topic,
        ],
        localizations: {
            amplifyButton: 'Monitor.leftSideBar.topics.amplifyButton',
            itemsHeading: 'Monitor.leftSideBar.topics.itemsHeading',
            statusHeading: '',
            noItems: 'Monitor.leftSideBar.topics.noItems'
        },
        quickFilterPlaceholder: 'Monitor.leftSidebar.topics.filter.quick',
        filterOptions: [
            { filterId: 'name', prefProp: 'name', label: 'Monitoring.search.topicName' },
            { filterId: 'autotags', prefProp: 'autoTag' },
            { filterId: 'autoSaving', prefProp: 'autoSaving' },
        ]
    },
    [Mode.mentions]: {
        routeTypes: [
            RouteType.folder,
            RouteType.mentions,
            RouteType.mentionsHype,
        ],
        localizations: {
            amplifyButton: 'Monitor.leftSideBar.savedMentions.amplifyButton',
            itemsHeading: 'Monitor.leftSideBar.savedMentions.itemsHeading',
            statusHeading: 'Monitor.leftSideBar.savedMentions.statusHeading',
            noItems: 'Monitor.leftSideBar.savedMentions.noItems'
        },
        quickFilterPlaceholder: 'Monitor.leftSidebar.savedMentions.filter.quick',
        filterOptions: [
            { filterId: 'name', prefProp: 'name', label: 'Monitoring.result.folderName' },
        ],
    },
    [Mode.briefings]: {
        routeTypes: [
            RouteType.briefingsHype,
            RouteType.briefing,
            RouteType.briefingReport,
            RouteType.recurringBriefingReport
        ],
        localizations: {
            amplifyButton: 'Monitor.leftSideBar.briefings.amplifyButton.newOnlineBriefing',
            secondaryAmplifyButton: 'Monitor.leftSideBar.briefings.amplifyButton.newEmailBriefing',
            itemsHeading: 'Monitor.leftSideBar.briefings.itemsHeading',
            statusHeading: 'Monitor.leftSideBar.briefings.statusHeading',
            noItems: 'Monitor.leftSideBar.briefings.noItems'
        },
        quickFilterPlaceholder: 'Monitor.leftSidebar.briefings.filter.quick',
        filterOptions: [
            { filterId: 'name', prefProp: 'name', label: 'Briefings.input.briefingName' },
            {
                filterId: 'status',
                prefProp: 'status',
                label: 'Monitor.leftSideBar.briefings.statusHeading',
                optionLabel: { prefix: 'Briefings.status.', enumType: BriefingStatusEnum }
            },
            {
                filterId: 'deliveryMethod',
                prefProp: 'deliveryMethod',
                label: 'Monitor.leftSidebar.briefings.type',
                optionLabel: { prefix: 'Monitor.leftSidebar.briefings.type.', enumType: BriefingDeliveryMethodEnum }
            }
        ]
    },
    [Mode.alerts]: {
        routeTypes: [
            RouteType.alertsHype,
            RouteType.alert,
            RouteType.recurringAlertReport
        ],
        localizations: {
            amplifyButton: 'Monitor.leftSideBar.alerts.amplifyButton',
            itemsHeading: 'Monitor.leftSideBar.alerts.itemsHeading',
            statusHeading: 'Monitor.leftSideBar.alerts.statusHeading',
            noItems: 'Monitor.leftSideBar.alerts.noItems'
        },
        quickFilterPlaceholder: 'Monitor.leftSidebar.alerts.filter.quick',
        filterOptions: [
            { filterId: 'name', prefProp: 'name', label: 'Alerts.input.alertName' },
            {
                filterId: 'status',
                prefProp: 'status',
                label: 'Monitor.leftSideBar.alerts.statusHeading',
                optionLabel: { prefix: 'Alerts.status.', enumType: AlertStatusEnum }
            },
            {
                filterId: 'alertType',
                prefProp: 'alertType',
                label: 'Alerts.alertType',
                optionLabel: { prefix: 'Alerts.alertType.', enumType: AlertTypeEnum }
            },
        ]
    },
    [Mode.mediaSubscriptions]: {
        routeTypes: [
            RouteType.subscriptionsHype,
            RouteType.subscription,
            RouteType.subscriptionDashboard,
            RouteType.subscriptionResults,
        ],
        localizations: {
            amplifyButton: 'Monitor.leftSideBar.subscriptions.amplifyButton',
            itemsHeading: 'Monitor.leftSideBar.subscriptions.itemsHeading',
            statusHeading: 'Monitor.leftSideBar.subscriptions.statusHeading',
            noItems: 'Monitor.leftSideBar.subscriptions.noItems'
        },
        quickFilterPlaceholder: 'Monitor.leftSidebar.subscriptions.filter.quick',
        filterOptions: [
            { filterId: 'name', prefProp: 'name', label: 'MediaSubscriptions.subscriptionName' },
            {
                filterId: 'status',
                prefProp: 'status',
                label: 'Monitor.leftSideBar.subscriptions.statusHeading',
                optionLabel: { prefix: 'MediaSubscriptions.status.', enumType: MediaSubscriptionStatusEnum }
            },
            {
                filterId: 'type',
                prefProp: 'type',
                label: 'Monitor.leftSideBar.subscriptions.typeFilter.heading',
                optionLabel: { prefix: 'MediaSubscriptionType.', enumType: allowedMediaSubscriptionEnum}
            }
        ]
    },
};

/**
 * Overall configuration managaer. Keeps a map of a mode to each config, and passes them all possible filters on register
 */
export class MonitoringLeftSidebarConfiguration {
    private monitoringLeftSidebarModeConfigMap = new Map<Mode, MonitoringLeftSidebarModeConfigOption<LeftSidebarListItemConvertableModel>>();
    private allFilters: FilterTypeModel[] = [];

    constructor(allFilters: FilterTypeModel[]) {
        this.allFilters = allFilters;
    }

    registerConfig<T extends LeftSidebarListItemConvertableModel>(
        mode: Mode,
        context: MonitoringLeftSidebarModeConfigContext<T>
    ) {
        const baseConfig = baseConfigMap[mode];

        this.monitoringLeftSidebarModeConfigMap.set(
            mode,
            new MonitoringLeftSidebarModeConfigOption<T>(
                baseConfig,
                context,
                this.allFilters,
            )
        );
    }

    getConfig(mode: Mode): MonitoringLeftSidebarModeConfigOption<LeftSidebarListItemConvertableModel> {
        return this.monitoringLeftSidebarModeConfigMap.get(mode);
    }
}

/**
 * Represents a single mode configuration object. Uses both the static config data (MonitoringLeftSidebarModeConfigBase),
 * and instance data passed from the LHP Service. Defines methods for handling filter setup and preparation.
 */
export class MonitoringLeftSidebarModeConfigOption<T extends LeftSidebarListItemConvertableModel> {
    isComplete: true;
    isLoading: true;
    isAmplifyButtonEnabled: true;
    readonly routeTypes: RouteType[];
    readonly listItemSource: T[];
    readonly listItemStream: Observable<T[]>;
    readonly convertToSidebarListItems: (items: T[]) => LeftSidebarListItem[];
    readonly localizations: LeftSidebarLocalizations;
    readonly quickFilterPlaceholder: string;
    private allFilters: FilterTypeModel[];
    private filterOptions: FilterOptions;
    topListItem?: LeftSidebarListItem;

    constructor(
        configOptions: MonitoringLeftSidebarModeConfigBase,
        context: MonitoringLeftSidebarModeConfigContext<T>,
        allFilters: FilterTypeModel[]
    ) {
        this.routeTypes = configOptions?.routeTypes;
        this.localizations = configOptions?.localizations;
        this.quickFilterPlaceholder = configOptions?.quickFilterPlaceholder;
        this.convertToSidebarListItems = context?.convertToSidebarListItems;
        this.listItemSource = context?.listItemSource;
        this.listItemStream = context?.listItemStream;
        this.allFilters = allFilters;
        this.filterOptions = commonFilterOptions.concat(configOptions?.filterOptions);
    }

    get filters(): FilterTypeModel[] {
        return this.allFilters.filter(filter => this.filterOptions.find(filterOption => filterOption?.filterId === filter.id));
    }

    setPreferenceFilters(userPreferenceFilters: UserAppPreferenceCommonFilters) {
        const filters = this.filters;
        filters.forEach(filter => {
            const filterProp = this.filterOptions.find(option => option.filterId === filter.id)?.prefProp;
            filter.setCurrentValue(userPreferenceFilters[filterProp]);
        });
    }

    prepareFilterLabels() {
        const labelFilters = this.filterOptions.filter(option => option?.label);
        labelFilters.forEach(option => {
            this.allFilters.find(filter => filter.id === option?.filterId).label = option.label;
        });
    }

    getFilterOptionLabel(id: string): FilterOptionEnumLabel {
        return this.filterOptions.find(option => option?.filterId === id)?.optionLabel;
    }
}

